import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Public, SEO-indexable landing page at /discover.
 * Shows the top dropship product + top gig + top task with outbound links.
 * Every card CTAs into signup so search-engine traffic funnels into VIP or free tier.
 */
export default function Discover() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/discover");
        setData(r?.data);
      } catch (e) {
        setErr("Couldn't load today's picks — check back in a minute.");
      }
    })();
    // Update the document title + meta description for SEO.
    const prevTitle = document.title;
    document.title = "Today's trending gig, product & task · MoneyHub VIP";
    let meta = document.querySelector('meta[name="description"]');
    const prevMeta = meta?.getAttribute("content");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "See today's highest-signal side-hustle gig, dropship product, and paid micro-task — refreshed daily by MoneyHub VIP's AI. Free to browse.",
    );
    return () => {
      document.title = prevTitle;
      if (prevMeta && meta) meta.setAttribute("content", prevMeta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-zinc-800/60 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="discover-home-link"
            className="text-sm font-heading font-black tracking-tighter hover:text-[#60a5fa] transition-colors"
          >
            ← MoneyHub VIP
          </Link>
          <Link
            to="/register"
            data-testid="discover-signup-cta"
            className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#22c55e] hover:bg-[#4ade80] px-4 py-2.5 font-black transition-colors rounded"
          >
            Sign up free
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-3">
          // Today's top picks · refreshed daily
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
          The highest-signal <span className="text-[#22c55e]">gig</span>,
          <br />
          <span className="text-[#a855f7]">product</span>, and{" "}
          <span className="text-[#60a5fa]">task</span> right now.
        </h1>
        <p className="mt-6 text-zinc-300 max-w-2xl text-sm md:text-base leading-relaxed">
          MoneyHub VIP scores 5 income streams every day. This is the top pick
          from three of them, free to browse. Save any of them to your dashboard
          for a 7-day AI action plan.
        </p>

        {err && (
          <div className="mt-8 border border-[#fb7185]/40 bg-[#fb7185]/10 text-[#fb7185] font-mono text-sm px-4 py-4 rounded">
            {err}
          </div>
        )}

        {data && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.top_gig && (
              <article
                data-testid="discover-top-gig"
                className="border border-[#22c55e]/40 bg-zinc-950/60 p-6 rounded"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] font-mono mb-3">
                  // Top gig
                </div>
                <h2 className="font-heading text-xl font-black mb-2 leading-tight">
                  {data.top_gig.title}
                </h2>
                <div className="text-xs text-zinc-300 font-mono mb-1">
                  {data.top_gig.platform}
                </div>
                <div className="text-lg font-heading font-black text-[#22c55e] mb-1">
                  {data.top_gig.payout}
                </div>
                <div className="text-xs text-zinc-300 font-mono">
                  Speed: {data.top_gig.speed} · Demand: {data.top_gig.demand}
                </div>
                <a
                  href={data.top_gig.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid="discover-top-gig-link"
                  className="mt-4 inline-block text-[10px] tracking-[0.3em] uppercase text-black bg-[#22c55e] hover:bg-[#4ade80] px-4 py-2 font-black rounded transition-colors"
                >
                  Open on {data.top_gig.platform} →
                </a>
              </article>
            )}

            {data.top_product && (
              <article
                data-testid="discover-top-product"
                className="border border-[#a855f7]/40 bg-zinc-950/60 p-6 rounded"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono mb-3">
                  // Top product
                </div>
                <h2 className="font-heading text-xl font-black mb-2 leading-tight">
                  {data.top_product.title}
                </h2>
                <div className="text-xs text-zinc-300 font-mono mb-1">
                  {data.top_product.category} · from {data.top_product.source}
                </div>
                <div className="text-lg font-heading font-black text-[#a855f7] mb-1">
                  {data.top_product.avg_price} · {data.top_product.margin} margin
                </div>
                <div className="text-xs text-zinc-300 font-mono">
                  Trend score: {data.top_product.trend_score}/100
                </div>
                <a
                  href={data.top_product.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid="discover-top-product-link"
                  className="mt-4 inline-block text-[10px] tracking-[0.3em] uppercase text-black bg-[#a855f7] hover:bg-[#c084fc] px-4 py-2 font-black rounded transition-colors"
                >
                  Find suppliers →
                </a>
              </article>
            )}

            {data.top_task && (
              <article
                data-testid="discover-top-task"
                className="border border-[#60a5fa]/40 bg-zinc-950/60 p-6 rounded"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-3">
                  // Top task
                </div>
                <h2 className="font-heading text-xl font-black mb-2 leading-tight">
                  {data.top_task.title}
                </h2>
                <div className="text-xs text-zinc-300 font-mono mb-1">
                  {data.top_task.platform}
                </div>
                <div className="text-lg font-heading font-black text-[#60a5fa] mb-1">
                  {data.top_task.payout}
                </div>
                <div className="text-xs text-zinc-300 font-mono">
                  Time: {data.top_task.time}
                </div>
                <a
                  href={data.top_task.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid="discover-top-task-link"
                  className="mt-4 inline-block text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#93c5fd] px-4 py-2 font-black rounded transition-colors"
                >
                  Start earning →
                </a>
              </article>
            )}
          </div>
        )}

        {/* Signup funnel */}
        <section className="mt-16 border-t border-zinc-800 pt-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mb-3">
            // Want the other 47 picks?
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter max-w-3xl">
            Get every stream, ranked, refreshed daily —{" "}
            <span className="text-[#22c55e]">free tier available.</span>
          </h2>
          <p className="mt-4 text-zinc-300 max-w-2xl text-sm md:text-base leading-relaxed">
            The full MoneyHub VIP dashboard scores 5 streams (side hustles,
            gigs, dropship products, crypto movers, micro-tasks) every day and
            generates a 7-day AI action plan for every opportunity you save.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/register"
              data-testid="discover-signup-cta-bottom"
              className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#22c55e] hover:bg-[#4ade80] px-6 py-4 font-black transition-colors rounded"
            >
              Sign up free →
            </Link>
            <Link
              to="/"
              data-testid="discover-vip-cta"
              className="text-[10px] tracking-[0.3em] uppercase text-white border border-[#60a5fa] hover:bg-[#60a5fa]/10 px-6 py-4 font-black transition-colors rounded"
            >
              Or go VIP · $9 →
            </Link>
          </div>
        </section>

        {/* Secondary rows for extra SEO surface */}
        {data && (
          <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-heading text-lg font-black mb-4 text-[#22c55e]">More gigs</h3>
              <ul className="space-y-3 text-sm">
                {data.gigs?.slice(1).map((g) => (
                  <li key={g.id}>
                    <a
                      href={g.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-zinc-300 hover:text-white font-mono"
                    >
                      → {g.title} <span className="text-zinc-500">· {g.payout}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-lg font-black mb-4 text-[#a855f7]">More products</h3>
              <ul className="space-y-3 text-sm">
                {data.products?.slice(1).map((p) => (
                  <li key={p.id}>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-zinc-300 hover:text-white font-mono"
                    >
                      → {p.title} <span className="text-zinc-500">· {p.margin}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-lg font-black mb-4 text-[#60a5fa]">More tasks</h3>
              <ul className="space-y-3 text-sm">
                {data.tasks?.slice(1).map((t) => (
                  <li key={t.id}>
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-zinc-300 hover:text-white font-mono"
                    >
                      → {t.title} <span className="text-zinc-500">· {t.payout}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-zinc-800 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap justify-between gap-4 text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-mono">
          <div>© 2026 MoneyHub VIP</div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
            <a href="mailto:moneyhubvip@outlook.com" className="hover:text-white transition-colors">Support</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
